import { CAMPAIGN_FILTER_OPTIONS } from "../data/contactsData";

function normalizeFilterToken(value) {
  return String(value || "").trim().toLowerCase();
}

function toSortedOptions(values) {
  return [...values]
    .sort((a, b) => a.localeCompare(b))
    .map((item) => ({ value: item, label: item }));
}

export function deriveContactFilterOptions(contacts) {
  const leadStatuses = new Set();
  const tags = new Set();

  contacts.forEach((contact) => {
    if (contact.leadStatus) {
      leadStatuses.add(contact.leadStatus);
    }
    (contact.tags || []).forEach((tag) => {
      if (tag) tags.add(tag);
    });
  });

  return {
    leadStatuses: toSortedOptions(leadStatuses),
    tags: toSortedOptions(tags),
  };
}

function mergeOptionLists(...lists) {
  const byValue = new Map();
  lists.flat().forEach((option) => {
    if (option?.value) {
      byValue.set(option.value, option);
    }
  });
  return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** Merge option pools so API-narrowed lists do not drop previously seen values. */
export function mergeContactFilterOptions(...optionSets) {
  return {
    leadStatuses: mergeOptionLists(...optionSets.map((set) => set?.leadStatuses || [])),
    tags: mergeOptionLists(...optionSets.map((set) => set?.tags || [])),
  };
}

/** Keep selected filter values visible even if they are not on the current page. */
export function optionsFromFilterValues(filters = []) {
  const leadStatuses = new Set();
  const tags = new Set();

  filters.forEach((filter) => {
    if (!filter.value) return;
    if (filter.field === "lead_status") {
      leadStatuses.add(filter.value);
    }
    if (filter.field === "tags") {
      tags.add(filter.value);
    }
  });

  return {
    leadStatuses: toSortedOptions(leadStatuses),
    tags: toSortedOptions(tags),
  };
}

export function getStatusColor(status) {
  const normalized = (status || "").toLowerCase();

  if (normalized === "new" || normalized === "new lead") {
    return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800";
  }
  if (normalized === "engaged" || normalized === "contacted") {
    return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800";
  }
  if (normalized === "qualified lead" || normalized === "qualified") {
    return "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800";
  }
  if (normalized === "customer" || normalized === "converted") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800";
  }
  if (normalized === "not interested") {
    return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
  }

  return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
}

export function normalizePhone(value) {
  return (value || "").replace(/\D/g, "");
}

export function matchesSearch(contact, searchQuery) {
  const query = (searchQuery || "").trim().toLowerCase();
  if (!query) return true;

  const phoneQuery = normalizePhone(searchQuery);
  const contactPhone = normalizePhone(contact.phone);
  const contactName = (contact.name || "").toLowerCase();

  return (
    contactName.includes(query) ||
    (phoneQuery && contactPhone.includes(phoneQuery))
  );
}

function matchesLeadStatus(contact, operator, value) {
  if (!value) return true;

  const contactStatus = normalizeFilterToken(contact.leadStatus);
  const filterValue = normalizeFilterToken(value);
  const matches =
    contactStatus === filterValue ||
    contactStatus.includes(filterValue) ||
    filterValue.includes(contactStatus);

  return operator === "is not" ? !matches : matches;
}

function matchesTags(contact, operator, value) {
  if (!value) return true;

  const tagNeedle = normalizeFilterToken(value);
  const tags = contact.tags || [];
  const hasTag = tags.some((tag) => normalizeFilterToken(tag) === tagNeedle);
  return operator === "does not include" ? !hasTag : hasTag;
}

function matchesSource(contact, operator, value) {
  if (!value) return true;

  const contactSource = normalizeFilterToken(contact.source);
  const filterValue = normalizeFilterToken(value);
  const matches =
    contactSource === filterValue ||
    contactSource.includes(filterValue) ||
    filterValue.includes(contactSource);

  return operator === "is not" ? !matches : matches;
}

function matchesSourceCampaign(contact, operator, value) {
  if (!value) return true;

  const campaign = CAMPAIGN_FILTER_OPTIONS.find((item) => item.value === value);
  const campaignId = campaign?.campaignId;
  const campaignLabel = (campaign?.label || value).toLowerCase();
  const contactSource = (contact.source || "").toLowerCase();

  const replied =
    contact.sourceCampaignId === campaignId ||
    contactSource.includes(campaignLabel);

  return operator === "did not reply to" ? !replied : replied;
}

function matchesCustomAttribute(contact, operator, value, attributeKey) {
  if (!attributeKey && !value) return true;

  const attributes = contact.customAttributes || [];
  const attribute = attributes.find(
    (item) => item.key.toLowerCase() === (attributeKey || "").trim().toLowerCase()
  );

  if (!attribute) {
    return operator === "is not";
  }

  const attrValue = (attribute.value || "").toLowerCase();
  const filterValue = (value || "").toLowerCase();

  if (operator === "contains") {
    return attrValue.includes(filterValue);
  }
  if (operator === "is not") {
    return attrValue !== filterValue;
  }

  return attrValue === filterValue;
}

export function applyContactFilter(contact, filter) {
  if (!filter.value && filter.field !== "custom_attribute") return true;
  if (filter.field === "custom_attribute" && !filter.attributeKey && !filter.value) return true;

  switch (filter.field) {
    case "lead_status":
      return matchesLeadStatus(contact, filter.operator, filter.value);
    case "tags":
      return matchesTags(contact, filter.operator, filter.value);
    case "source":
      return matchesSource(contact, filter.operator, filter.value);
    case "source_campaign":
      return matchesSourceCampaign(contact, filter.operator, filter.value);
    case "custom_attribute":
      return matchesCustomAttribute(
        contact,
        filter.operator,
        filter.value,
        filter.attributeKey
      );
    default:
      return true;
  }
}

export function filterContacts(contacts, { searchQuery = "", filters = [] } = {}) {
  const activeFilters = filters.filter(
    (filter) =>
      filter.value ||
      (filter.field === "custom_attribute" && filter.attributeKey)
  );

  return contacts.filter((contact) => {
    if (!matchesSearch(contact, searchQuery)) return false;
    return activeFilters.every((filter) => applyContactFilter(contact, filter));
  });
}

export function hasValidFilters(filters) {
  return filters.some(
    (filter) =>
      filter.value ||
      (filter.field === "custom_attribute" && filter.attributeKey)
  );
}

export function getDefaultOperatorForField(field) {
  switch (field) {
    case "tags":
      return "includes";
    case "source_campaign":
      return "replied to";
    default:
      return "is";
  }
}

export function getCampaignNameById(campaignId) {
  const campaign = CAMPAIGN_FILTER_OPTIONS.find((item) => item.campaignId === campaignId);
  return campaign?.label || "Campaign";
}
